// ChartView.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getNodeSubtitle, isSubtitleFieldKey } from 'utilities/NodeSubtitle';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

// CSS
import './ChartView.css';

// Components
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const CHART_COLORS = ['#2563eb', '#14b8a6', '#f59e0b', '#ef4444', '#7c3aed', '#0ea5e9', '#84cc16'];

const TEMPORAL_FIELD_TYPES = new Set(['date', 'datetime', 'daterange', 'datetimerange']);

const toComparableDate = (value) => {
    if (value == null) return null;
    if (value instanceof Date) {
        const timestamp = value.getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
    }
    if (value?.toDate && typeof value.toDate === 'function') {
        const date = value.toDate();
        const timestamp = date?.getTime?.();
        return Number.isFinite(timestamp) ? timestamp : null;
    }
    if (value?.seconds != null) {
        const seconds = Number(value.seconds);
        if (Number.isFinite(seconds)) {
            const nanos = Number(value?.nanoseconds || 0);
            return (seconds * 1000) + Math.floor((Number.isFinite(nanos) ? nanos : 0) / 1e6);
        }
    }
    const parsed = new Date(value);
    const timestamp = parsed.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const normalizeDimensionValue = (rawValue) => {
    if (rawValue == null || rawValue === '') return '(empty)';
    if (Array.isArray(rawValue)) {
        const values = rawValue
            .map((value) => normalizeDimensionValue(value))
            .filter(Boolean);
        return values.length ? values.join(', ') : '(empty)';
    }
    if (typeof rawValue === 'object') {
        if (rawValue?.title) return String(rawValue.title);
        if (rawValue?.name) return String(rawValue.name);
        if (rawValue?.label) return String(rawValue.label);
        if (rawValue?.key) return String(rawValue.key);
        const dateValue = toComparableDate(rawValue);
        if (dateValue != null) return new Date(dateValue).toLocaleDateString();
        return '(object)';
    }
    const dateValue = toComparableDate(rawValue);
    if (dateValue != null && typeof rawValue !== 'number') {
        return new Date(dateValue).toLocaleDateString();
    }
    return String(rawValue);
};

const toNumericValue = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const next = Number(value);
    return Number.isFinite(next) ? next : null;
};

const startOfWeek = (date) => {
    const next = new Date(date.getTime());
    next.setHours(0, 0, 0, 0);
    next.setDate(next.getDate() - next.getDay());
    return next;
};

const toDayStart = (date) => {
    const next = new Date(date.getTime());
    next.setHours(0, 0, 0, 0);
    return next;
};

const getTemporalBucket = ({ timestamp, bin }) => {
    const date = new Date(timestamp);

    if (bin === 'week') {
        const weekStart = startOfWeek(date);
        return {
            key: `w:${weekStart.getFullYear()}-${weekStart.getMonth() + 1}-${weekStart.getDate()}`,
            label: `Week of ${weekStart.toLocaleDateString()}`,
            sortValue: weekStart.getTime(),
        };
    }

    if (bin === 'month') {
        const year = date.getFullYear();
        const month = date.getMonth();
        return {
            key: `m:${year}-${month + 1}`,
            label: date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
            sortValue: new Date(year, month, 1).getTime(),
        };
    }

    if (bin === 'quarter') {
        const year = date.getFullYear();
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return {
            key: `q:${year}-Q${quarter}`,
            label: `Q${quarter} ${year}`,
            sortValue: new Date(year, (quarter - 1) * 3, 1).getTime(),
        };
    }

    if (bin === 'year') {
        const year = date.getFullYear();
        return {
            key: `y:${year}`,
            label: String(year),
            sortValue: new Date(year, 0, 1).getTime(),
        };
    }

    const day = toDayStart(date);
    return {
        key: `d:${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`,
        label: day.toLocaleDateString(),
        sortValue: day.getTime(),
    };
};

const resolveNodeFieldValue = ({ node, fieldKey, fieldDef, typeDef }) => {
    if (!fieldKey || !node) return undefined;
    if (fieldKey === 'title') return node?.title;
    if (fieldKey === 'autonumber') return node?.autonumber;
    if (fieldKey === 'autokey') return node?.autokey;
    if (isSubtitleFieldKey(fieldKey, fieldDef?.type)) return getNodeSubtitle(node);
    if (fieldKey === 'dateCreated') return node?.dateCreated;
    if (fieldKey === 'dateModified') return node?.dateModified;

    if (!fieldDef) return node?.[fieldKey];
    return resolveSchemaFieldValue({
        item: node,
        fieldDef,
        typeKey: typeDef?.key,
        dataSection: typeDef?.dataSection,
    });
};

/**
 * ChartView
 * 
 * Renders a chart view based on the provided configuration and data. This component is responsible 
 * for displaying various types of charts (e.g., bar, line, pie).
 *  
 *
 * @returns {ReactNode} - ChartView component.
 */
const ChartView = React.memo(({ view, typeDef, nodes = [], displayMode = 'full' }) => {
    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const isCompact = displayMode === 'compact';

    const chartConfig = view?.chartConfig && typeof view.chartConfig === 'object'
        ? view.chartConfig
        : {};

    const chartType = String(chartConfig?.chartType || 'bar').toLowerCase();
    const aggregation = String(chartConfig?.aggregation || 'count').toLowerCase();
    const xField = String(chartConfig?.xField || '').trim();
    const yField = String(chartConfig?.yField || '').trim();
    const groupField = String(chartConfig?.groupField || '').trim();
    const showLegend = isCompact ? false : (chartConfig?.showLegend == null ? true : Boolean(chartConfig?.showLegend));
    const showGrid = chartConfig?.showGrid == null ? true : Boolean(chartConfig?.showGrid);
    const fontFamily = chartConfig?.fontFamily || 'inherit';
    const yAxisFormat = String(chartConfig?.yAxisFormat || 'number').toLowerCase();
    const currencyCode = String(chartConfig?.currencyCode || 'USD').toUpperCase();
    const xDateBin = String(chartConfig?.xDateBin || 'auto').toLowerCase();

    const backgroundColor = chartConfig?.backgroundColor || theme.panel || theme.background;
    const textColor = chartConfig?.textColor || theme.foreground;
    const gridColor = chartConfig?.gridColor || theme.backgroundMuted;
    const seriesColorMap = chartConfig?.seriesColors && typeof chartConfig.seriesColors === 'object'
        ? chartConfig.seriesColors
        : {};

    // ----- UTILITIES ----- //

    const resolveSeriesColor = (seriesKey, fallbackIndex) => {
        const normalizedKey = String(seriesKey || '').trim();
        const override = normalizedKey ? String(seriesColorMap?.[normalizedKey] || '').trim() : '';
        return override || CHART_COLORS[fallbackIndex % CHART_COLORS.length];
    };

    // ----- MEMOS ----- //

    const schemaFieldMap = useMemo(() => {
        const map = new Map();
        const rootFields = [
            { key: 'title', name: 'title', label: 'Title' },
            { key: 'autonumber', name: 'autonumber', label: 'ID', type: 'number' },
            { key: 'autokey', name: 'autokey', label: 'Key', type: 'string' },
            { key: 'subtitle', name: 'subtitle', label: 'Subtitle', type: 'subtitle' },
            { key: 'dateCreated', name: 'dateCreated', label: 'Date Created', type: 'date' },
            { key: 'dateModified', name: 'dateModified', label: 'Date Modified', type: 'date' },
        ];

        rootFields.forEach((field) => {
            map.set(field.key, field);
        });

        const schemaFields = resolveDisplaySchemaFields(typeDef, { includeHidden: true });

        (Array.isArray(schemaFields) ? schemaFields : []).forEach((field) => {
            const key = field?.name || field?.key;
            if (key) map.set(key, field);
        });
        return map;
    }, [typeDef]);

    const canRender = Boolean(xField) && (aggregation === 'count' || Boolean(yField));

    const xFieldType = useMemo(() => {
        const fieldType = String(schemaFieldMap.get(xField)?.type || '').toLowerCase();
        if (xField === 'dateCreated' || xField === 'dateModified') return 'date';
        return fieldType;
    }, [schemaFieldMap, xField]);
    const isTemporalXAxis = TEMPORAL_FIELD_TYPES.has(xFieldType);

    const formatYAxisValue = useMemo(() => {
        if (yAxisFormat === 'compact') {
            const formatter = new Intl.NumberFormat(undefined, {
                notation: 'compact',
                maximumFractionDigits: 1,
            });
            return (value) => formatter.format(Number(value || 0));
        }

        if (yAxisFormat === 'currency') {
            let formatter;
            try {
                formatter = new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: currencyCode || 'USD',
                    maximumFractionDigits: 2,
                });
            } catch (error) {
                formatter = new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 2,
                });
            }
            return (value) => formatter.format(Number(value || 0));
        }

        if (yAxisFormat === 'percent') {
            return (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
        }

        return (value) => Number(value || 0).toLocaleString();
    }, [currencyCode, yAxisFormat]);

    const { data, seriesKeys } = useMemo(() => {
        if (!canRender) return { data: [], seriesKeys: [] };

        const bucketMap = new Map();
        const bucketMetaMap = new Map();
        const encounteredSeries = new Set();
        const safeNodes = Array.isArray(nodes) ? nodes : [];

        const explicitBin = xDateBin === 'day'
            || xDateBin === 'week'
            || xDateBin === 'month'
            || xDateBin === 'quarter'
            || xDateBin === 'year'
            ? xDateBin
            : 'auto';

        let effectiveTemporalBin = explicitBin;
        if (isTemporalXAxis && explicitBin === 'auto') {
            const timestamps = safeNodes
                .map((node) => {
                    const xDef = schemaFieldMap.get(xField) || null;
                    const rawX = resolveNodeFieldValue({ node, fieldKey: xField, fieldDef: xDef, typeDef });
                    return toComparableDate(rawX);
                })
                .filter((value) => Number.isFinite(value));

            if (timestamps.length > 1) {
                const minTs = Math.min(...timestamps);
                const maxTs = Math.max(...timestamps);
                const spanDays = (maxTs - minTs) / (1000 * 60 * 60 * 24);
                if (spanDays > 365 * 2) effectiveTemporalBin = 'year';
                else if (spanDays > 180) effectiveTemporalBin = 'quarter';
                else if (spanDays > 45) effectiveTemporalBin = 'month';
                else if (spanDays > 14) effectiveTemporalBin = 'week';
                else effectiveTemporalBin = 'day';
            } else {
                effectiveTemporalBin = 'day';
            }
        }

        safeNodes.forEach((node) => {
            const xDef = schemaFieldMap.get(xField) || null;
            const yDef = schemaFieldMap.get(yField) || null;
            const groupDef = schemaFieldMap.get(groupField) || null;

            const rawX = resolveNodeFieldValue({ node, fieldKey: xField, fieldDef: xDef, typeDef });
            let bucketKey = '';
            let bucketLabel = '';
            let bucketSort = 0;

            if (isTemporalXAxis) {
                const timestamp = toComparableDate(rawX);
                if (timestamp == null) {
                    bucketKey = '__empty__';
                    bucketLabel = '(empty)';
                    bucketSort = Number.NEGATIVE_INFINITY;
                } else {
                    const bucket = getTemporalBucket({
                        timestamp,
                        bin: effectiveTemporalBin === 'auto' ? 'day' : effectiveTemporalBin,
                    });
                    bucketKey = bucket.key;
                    bucketLabel = bucket.label;
                    bucketSort = bucket.sortValue;
                }
            } else {
                const xLabel = normalizeDimensionValue(rawX);
                bucketKey = `raw:${xLabel}`;
                bucketLabel = xLabel;
                bucketSort = xLabel.toLowerCase();
            }

            const rawGroup = groupField
                ? resolveNodeFieldValue({ node, fieldKey: groupField, fieldDef: groupDef, typeDef })
                : null;
            const seriesLabel = groupField ? normalizeDimensionValue(rawGroup) : 'Value';
            encounteredSeries.add(seriesLabel);

            if (!bucketMap.has(bucketKey)) {
                bucketMap.set(bucketKey, new Map());
                bucketMetaMap.set(bucketKey, {
                    label: bucketLabel,
                    sortValue: bucketSort,
                });
            }
            const seriesMap = bucketMap.get(bucketKey);
            if (!seriesMap.has(seriesLabel)) {
                seriesMap.set(seriesLabel, {
                    count: 0,
                    sum: 0,
                    min: null,
                    max: null,
                });
            }

            const state = seriesMap.get(seriesLabel);
            state.count += 1;

            const yValue = aggregation === 'count'
                ? null
                : toNumericValue(resolveNodeFieldValue({ node, fieldKey: yField, fieldDef: yDef, typeDef }));

            if (aggregation !== 'count' && yValue == null) return;

            if (yValue != null) {
                state.sum += yValue;
                state.min = state.min == null ? yValue : Math.min(state.min, yValue);
                state.max = state.max == null ? yValue : Math.max(state.max, yValue);
            }
        });

        const keys = Array.from(encounteredSeries);
        const rows = Array.from(bucketMap.entries()).map(([bucketKey, seriesMap]) => {
            const bucketMeta = bucketMetaMap.get(bucketKey) || {};
            const row = {
                label: bucketMeta.label || '(empty)',
                _sortValue: bucketMeta.sortValue,
            };
            keys.forEach((seriesKey) => {
                const state = seriesMap.get(seriesKey);
                if (!state) {
                    row[seriesKey] = 0;
                    return;
                }

                if (aggregation === 'count') {
                    row[seriesKey] = state.count;
                    return;
                }
                if (aggregation === 'sum') {
                    row[seriesKey] = state.sum;
                    return;
                }
                if (aggregation === 'avg') {
                    row[seriesKey] = state.count > 0 ? (state.sum / state.count) : 0;
                    return;
                }
                if (aggregation === 'min') {
                    row[seriesKey] = state.min == null ? 0 : state.min;
                    return;
                }
                if (aggregation === 'max') {
                    row[seriesKey] = state.max == null ? 0 : state.max;
                    return;
                }
                row[seriesKey] = state.count;
            });
            return row;
        });

        rows.sort((a, b) => {
            const left = a?._sortValue;
            const right = b?._sortValue;
            if (typeof left === 'number' && typeof right === 'number') {
                return left - right;
            }
            return String(a?.label || '').localeCompare(String(b?.label || ''), undefined, { sensitivity: 'base' });
        });

        rows.forEach((row) => {
            delete row._sortValue;
        });

        return {
            data: rows,
            seriesKeys: keys,
        };
    }, [aggregation, canRender, groupField, isTemporalXAxis, nodes, schemaFieldMap, typeDef, xDateBin, xField, yField]);

    const tooltipStyle = {
        border: `1px solid ${gridColor}`,
        backgroundColor,
        color: textColor,
        fontFamily,
    };

    const axisStyle = {
        fill: textColor,
        fontSize: isCompact ? 10 : 12,
        fontFamily,
    };

    // ----- UTILITIES ----- //

    const renderSeriesChart = () => {
        const commonOverlays = (
            <>
                {showGrid && <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />}
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, name) => [formatYAxisValue(value), name]}
                />
                {showLegend && <Legend wrapperStyle={{ color: textColor, fontFamily }} />}
            </>
        );

        if (chartType === 'line') {
            return (
                <LineChart data={data}>
                    {commonOverlays}
                    <XAxis dataKey="label" tick={axisStyle} stroke={textColor} />
                    <YAxis tick={axisStyle} stroke={textColor} tickFormatter={formatYAxisValue} />
                    {seriesKeys.map((seriesKey, index) => (
                        <Line
                          key={seriesKey}
                          type="monotone"
                          dataKey={seriesKey}
                          stroke={resolveSeriesColor(seriesKey, index)}
                          strokeWidth={2}
                          dot={false}
                        />
                    ))}
                </LineChart>
            );
        }

        if (chartType === 'area') {
            return (
                <AreaChart data={data}>
                    {commonOverlays}
                    <XAxis dataKey="label" tick={axisStyle} stroke={textColor} />
                    <YAxis tick={axisStyle} stroke={textColor} tickFormatter={formatYAxisValue} />
                    {seriesKeys.map((seriesKey, index) => (
                        <Area
                          key={seriesKey}
                          type="monotone"
                          dataKey={seriesKey}
                          stroke={resolveSeriesColor(seriesKey, index)}
                          fill={resolveSeriesColor(seriesKey, index)}
                          fillOpacity={0.28}
                        />
                    ))}
                </AreaChart>
            );
        }

        if (chartType === 'pie') {
            const pieData = data.map((row) => {
                const value = seriesKeys.reduce((sum, key) => sum + Number(row?.[key] || 0), 0);
                return {
                    name: row.label,
                    value,
                };
            });

            return (
                <PieChart>
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(value, name) => [formatYAxisValue(value), name]}
                    />
                    {showLegend && <Legend wrapperStyle={{ color: textColor, fontFamily }} />}
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={isCompact ? '64%' : '70%'}
                      label={!isCompact}
                    >
                        {pieData.map((entry, index) => (
                            <Cell key={`${entry.name}_${index}`} fill={resolveSeriesColor(entry.name, index)} />
                        ))}
                    </Pie>
                </PieChart>
            );
        }

        return (
            <BarChart data={data}>
                {showGrid && <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />}
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, name) => [formatYAxisValue(value), name]}
                />
                {showLegend && <Legend wrapperStyle={{ color: textColor, fontFamily }} />}
                <XAxis dataKey="label" tick={axisStyle} stroke={textColor} />
                <YAxis tick={axisStyle} stroke={textColor} tickFormatter={formatYAxisValue} />
                {seriesKeys.map((seriesKey, index) => (
                    <Bar
                      key={seriesKey}
                      dataKey={seriesKey}
                      fill={resolveSeriesColor(seriesKey, index)}
                      radius={[4, 4, 0, 0]}
                    />
                ))}
            </BarChart>
        );
    };

    // ----- RENDER ----- //

    return (
        <div
          className={`chart-view-container ${isCompact ? 'chart-view-container-compact' : ''}`}
          style={{
                backgroundColor,
                borderColor: gridColor,
                color: textColor,
            }}
        >

            {/* EMPTY */}
            {!canRender && (

                <div
                  className="chart-view-empty"
                  style={{
                    color: textColor,
                  }}
                >
                    Select a category field and chart options in View settings.
                </div>
            )}

            {/* EMPTY */}
            {canRender && data.length === 0 && (

                <div
                  className="chart-view-empty"
                  style={{
                    color: textColor,
                  }}
                >
                    No chartable data with the current configuration.
                </div>
            )}

            {canRender && data.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                    {renderSeriesChart()}
                </ResponsiveContainer>
            )}
        </div>
    );

});

// Dev Tools
ChartView.displayName = 'ChartView';

export default ChartView;
